import {
  ConsentTrackingStatus,
  SourceType,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionButton,
  TandemLogoText,
  DexcomLogoText,
  NightscoutLogoText,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"

import styles from "./AddDataSourceButtons.module.scss"
import { useAddDataSource } from "./useAddDataSource"
import {
  useConsents,
  useAccountCountry,
  useColorScheme,
} from "src/hooks"

interface Props {
  isOnboarding?: boolean;
}

export function AddDataSourceButtons(props: Props) {
  const { isOnboarding } = props

  const country = useAccountCountry()
  const { data: consents } = useConsents()
  const addDataSource = useAddDataSource(isOnboarding)
  const navigate = useNavigate()

  const colorScheme = useColorScheme()

  const openNextDialog = useCallback(
    (chosenSource: SourceType.Dexcom | SourceType.Nightscout | SourceType.Tandem) => {
      const nppConsentStatus = consents?.npp_consent.status

      // Show NPP consent if the user has not acknowledged it yet
      if (nppConsentStatus === ConsentTrackingStatus.Accepted || nppConsentStatus === ConsentTrackingStatus.Declined) {
        addDataSource(chosenSource)
      } else {
        const path = isOnboarding ? "/onboarding/data_source/data_consent/$sourceType" : "/account/data_source/data_consent/$sourceType"

        void navigate({
          to: path,
          params: { sourceType: chosenSource },
        })
      }
    },
    [
      consents,
      addDataSource,
      navigate,
      isOnboarding,
    ],
  )

  const openTandemConsent = useCallback(
    () => {
      openNextDialog(SourceType.Tandem)
    },
    [openNextDialog],
  )

  const openDexcomConsent = useCallback(
    () => {
      openNextDialog(SourceType.Dexcom)
    },
    [openNextDialog],
  )

  const openNightscoutConsent = useCallback(
    () => {
      openNextDialog(SourceType.Nightscout)
    },
    [openNextDialog],
  )

  return (
    <div className={styles.addButtonContainer}>
      {country?.is_tandem && (
        <ActionButton
          stylePreset={colorScheme === "dark" ? "primary" : "secondary"}
          className={styles.addButton}
          id="tandem-button"
          onClick={openTandemConsent}
        >
          <TandemLogoText
            className={styles.tandemSvg}
            height={undefined}
            width={undefined}
          />
        </ActionButton>
      )}
      {country?.is_dexcom && (
        <ActionButton
          stylePreset={colorScheme === "dark" ? "primary" : "secondary"}
          className={styles.addButton}
          id="dexcom-button"
          onClick={openDexcomConsent}
        >
          <DexcomLogoText
            height={undefined}
            width={undefined}
            className={styles.dexcomSvg}
          />
        </ActionButton>
      )}
      <ActionButton
        stylePreset={colorScheme === "dark" ? "primary" : "secondary"}
        className={styles.addButton}
        id="nightscout-button"
        onClick={openNightscoutConsent}
      >
        <NightscoutLogoText
          height={undefined}
          width={undefined}
          className={styles.nightscoutSvg}
        />
      </ActionButton>
    </div>
  )
}
